import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/common/Input';
import { validateEmail, validateRequired } from '../../utils/validators';
import { formatPhoneInput } from '../../utils/formatters';
import { STORAGE_KEYS } from '../../utils/constants';
import './Signup.css';

const BUSINESS_TYPES = [
  'Bakery',
  'Restaurant',
  'Retailer',
  'Distributor',
  'Other',
];

const FLOUR_TYPES = [
  { value: 'bread', label: 'Bread Flour' },
  { value: 'all_purpose', label: 'All Purpose' },
  { value: 'high_protein', label: 'High Protein' },
  { value: 'rye', label: 'Rye' },
  { value: 'spelt', label: 'Spelt' },
  { value: 'buckwheat', label: 'Buckwheat' },
  { value: 'cornmeal', label: 'Cornmeal / Grits' },
  { value: 'oats', label: 'Rolled Oats' },
  { value: 'other', label: 'Other' },
];

const VOLUME_OPTIONS = [
  { value: '50lb', label: 'Up to 50 lbs / month' },
  { value: '100lb', label: 'Up to 100 lbs / month' },
  { value: '500lb', label: 'Up to 500 lbs / month' },
  { value: '1000lb_plus', label: '1,000+ lbs / month' },
];

const HIGH_VOLUME = new Set(['500lb', '1000lb_plus']);

const Signup = () => {
  const navigate = useNavigate();
  const { signup, isAuthenticated, error: authError, clearError, isSigningUp } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    phone: '',
    business_types: [],
    business_type_other: '',
    flour_types: [],
    current_supplier: '',
    estimated_monthly_volume: '',
    has_loading_dock: null,
    how_did_you_hear: '',
  });

  const [errors, setErrors] = useState({});
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(searchParams.get('pending') === 'true');

  // ── Sample cart from catalog ─────────────────────────────────────────────
  const [sampleCart, setSampleCart] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SAMPLE_CART);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const removeSampleItem = (variantId) => {
    setSampleCart(prev => prev.filter(i => i.productVariantId !== variantId));
  };

  const showLoadingDock = HIGH_VOLUME.has(formData.estimated_monthly_volume);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => { if (clearError) clearError(); };
  }, [clearError]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    }
    if (authError && clearError) clearError();
  };

  const handleFlourToggle = (value) => {
    setFormData(prev => {
      const current = prev.flour_types;
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, flour_types: next };
    });
    if (errors.flour_types) {
      setErrors(prev => { const next = { ...prev }; delete next.flour_types; return next; });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!validateRequired(formData.email)) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email)) newErrors.email = 'Please enter a valid email address';

    if (!validateRequired(formData.first_name)) newErrors.first_name = 'First name is required';
    if (!validateRequired(formData.last_name)) newErrors.last_name = 'Last name is required';
    if (!validateRequired(formData.company_name)) newErrors.company_name = 'Company name is required';

    if (formData.business_types.length === 0) {
      newErrors.business_types = 'Please select at least one business type';
    }
    if (formData.business_types.includes('Other') && !formData.business_type_other.trim()) {
      newErrors.business_type_other = 'Please describe your business type';
    }

    if (formData.flour_types.length === 0) {
      newErrors.flour_types = 'Please select at least one flour type';
    }

    if (!formData.estimated_monthly_volume) {
      newErrors.estimated_monthly_volume = 'Please select an estimated monthly volume';
    }

    if (showLoadingDock && formData.has_loading_dock === null) {
      newErrors.has_loading_dock = 'Please indicate whether you have a loading dock';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = { ...formData };
    if (!showLoadingDock) payload.has_loading_dock = null;

    // Include sample requests if any were collected from the catalog
    if (sampleCart.length > 0) {
      payload.sample_requests = sampleCart.map(item => ({
        product_variant_id: item.productVariantId,
        product_name: item.productName,
        variant_name: item.variantName,
        quantity: item.quantity,
      }));
    }

    try {
      await signup(payload);
      // Clear the sample cart from localStorage on successful submission
      try {
        localStorage.removeItem(STORAGE_KEYS.SAMPLE_CART);
      } catch {
        // ignore
      }
      setSubmitted(true);
    } catch (err) {
      const fieldErrors = err?.field_errors || err?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const mapped = {};
        for (const [key, messages] of Object.entries(fieldErrors)) {
          if (Array.isArray(messages) && messages.length) {
            mapped[key] = messages[0];
          } else if (typeof messages === 'string') {
            mapped[key] = messages;
          }
        }
        if (Object.keys(mapped).length) {
          setErrors(prev => ({ ...prev, ...mapped }));
          clearError();
        }
      }
    }
  };

  if (submitted) {
    return (
      <div className="signup-page">
        <div className="signup-page__container">
          <div className="signup-page__card">
            <div className="signup-page__header">
              <p className="signup-page__wordmark">Deep Roots Milling</p>
              <div className="signup-page__divider">
                <span className="signup-page__divider-line" />
                <span className="signup-page__divider-ornament">❧</span>
                <span className="signup-page__divider-line" />
              </div>
              <h1 className="signup-page__title">Application Received</h1>
              <p className="signup-page__subtitle">Thank you for your interest</p>
            </div>

            <div className="signup-page__success">
              <div className="signup-page__success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </div>
              <p className="signup-page__success-heading">Your application is pending review</p>
              <p className="signup-page__success-text">
                Our team will review your application and you'll receive an email with
                a link to set your password once your account has been approved.
              </p>
            </div>

            <div className="signup-page__footer">
              <p>
                <Link to="/login" className="signup-page__link">Back to Sign In</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-page__container">
        <div className="signup-page__card">

          {/* Header */}
          <div className="signup-page__header">
            <p className="signup-page__wordmark">Deep Roots Milling</p>
            <div className="signup-page__divider">
              <span className="signup-page__divider-line" />
              <span className="signup-page__divider-ornament">❧</span>
              <span className="signup-page__divider-line" />
            </div>
            <h1 className="signup-page__title">Apply for Wholesale Access</h1>
            <p className="signup-page__subtitle">Tell us about your operation — we'll be in touch</p>
          </div>

          {/* ── Sample request summary ── */}
          {sampleCart.length > 0 && (
            <div className="signup-page__sample-summary">
              <p className="signup-page__sample-summary-heading">Sample Requests</p>
              <p className="signup-page__sample-summary-hint">
                These will be noted on your application. Samples are sent once your account is approved.
              </p>
              <ul className="signup-page__sample-list">
                {sampleCart.map(item => (
                  <li key={item.productVariantId} className="signup-page__sample-item">
                    <span className="signup-page__sample-name">
                      {item.productName} — {item.variantName}
                    </span>
                    <button
                      type="button"
                      className="signup-page__sample-remove"
                      onClick={() => removeSampleItem(item.productVariantId)}
                      aria-label={`Remove ${item.variantName} from sample requests`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {authError && (
            <div className="signup-page__auth-error" role="alert">
              {authError.message || 'Submission failed. Please try again.'}
            </div>
          )}

          <form className="signup-page__form auth-form" onSubmit={handleSubmit} noValidate>

            {/* ── Personal Information ── */}
            <div className="signup-page__section">
              <p className="signup-page__section-eyebrow">Personal Information</p>
              <div className="signup-page__row">
                <Input
                  type="text"
                  name="first_name"
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  error={errors.first_name}
                  placeholder="Jane"
                  required
                  autoFocus
                />
                <Input
                  type="text"
                  name="last_name"
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  error={errors.last_name}
                  placeholder="Smith"
                  required
                />
              </div>
              <Input
                type="email"
                name="email"
                label="Email Address"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={errors.email}
                placeholder="you@company.com"
                autoComplete="email"
                required
              />
            </div>

            {/* ── Company Information ── */}
            <div className="signup-page__section">
              <p className="signup-page__section-eyebrow">Company Information</p>
              <Input
                type="text"
                name="company_name"
                label="Company Name"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                error={errors.company_name}
                placeholder="Acme Bakery"
                required
              />
              <Input
                type="tel"
                name="phone"
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleChange('phone', formatPhoneInput(e.target.value))}
                error={errors.phone}
                placeholder="555-555-5555"
                autoComplete="tel"
                maxLength={12}
              />
            </div>

            {/* ── Business Type ── */}
            <div className="signup-page__section">
              <p className="signup-page__section-eyebrow">
                Business Type <span className="signup-page__required">*</span>
              </p>
              <p className="signup-page__section-hint">Select all that apply</p>
              <div className="signup-page__pill-group">
                {BUSINESS_TYPES.map(type => (
                  <button
                    key={type}
                    type="button"
                    className={`signup-page__pill${formData.business_types.includes(type) ? ' signup-page__pill--selected' : ''}`}
                    onClick={() => {
                      const current = formData.business_types;
                      const next = current.includes(type)
                        ? current.filter(t => t !== type)
                        : [...current, type];
                      handleChange('business_types', next);
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
              {errors.business_types && (
                <p className="signup-page__field-error">{errors.business_types}</p>
              )}
              {formData.business_types.includes('Other') && (
                <div className="signup-page__conditional">
                  <Input
                    type="text"
                    name="business_type_other"
                    label="Please describe your business"
                    value={formData.business_type_other}
                    onChange={(e) => handleChange('business_type_other', e.target.value)}
                    error={errors.business_type_other}
                    placeholder="e.g. Food truck, catering, farmers market vendor…"
                    required
                  />
                </div>
              )}
            </div>

            {/* ── Flour Types ── */}
            <div className="signup-page__section">
              <p className="signup-page__section-eyebrow">
                Flour Types Used <span className="signup-page__required">*</span>
              </p>
              <p className="signup-page__section-hint">Select all that apply</p>
              <div className="signup-page__pill-group">
                {FLOUR_TYPES.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`signup-page__pill${formData.flour_types.includes(opt.value) ? ' signup-page__pill--selected' : ''}`}
                    onClick={() => handleFlourToggle(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {errors.flour_types && (
                <p className="signup-page__field-error">{errors.flour_types}</p>
              )}
            </div>

            {/* ── Current Supplier ── */}
            <div className="signup-page__section">
              <p className="signup-page__section-eyebrow">
                Current Flour Supplier <span className="signup-page__optional">(optional)</span>
              </p>
              <Input
                type="text"
                name="current_supplier"
                label="Current Supplier"
                value={formData.current_supplier}
                onChange={(e) => handleChange('current_supplier', e.target.value)}
                error={errors.current_supplier}
                placeholder="e.g. King Arthur, local mill, etc."
              />
            </div>

            {/* ── Estimated Monthly Volume ── */}
            <div className="signup-page__section">
              <p className="signup-page__section-eyebrow">
                Estimated Monthly Usage <span className="signup-page__required">*</span>
              </p>
              <div className="signup-page__pill-group">
                {VOLUME_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`signup-page__pill${formData.estimated_monthly_volume === opt.value ? ' signup-page__pill--selected' : ''}`}
                    onClick={() => handleChange('estimated_monthly_volume', opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {errors.estimated_monthly_volume && (
                <p className="signup-page__field-error">{errors.estimated_monthly_volume}</p>
              )}

              {showLoadingDock && (
                <div className="signup-page__conditional">
                  <p className="signup-page__section-label">
                    Do you have access to a commercial loading dock?{' '}
                    <span className="signup-page__required">*</span>
                  </p>
                  <div className="signup-page__pill-group">
                    <button
                      type="button"
                      className={`signup-page__pill${formData.has_loading_dock === true ? ' signup-page__pill--selected' : ''}`}
                      onClick={() => handleChange('has_loading_dock', true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`signup-page__pill${formData.has_loading_dock === false ? ' signup-page__pill--selected' : ''}`}
                      onClick={() => handleChange('has_loading_dock', false)}
                    >
                      No
                    </button>
                  </div>
                  {errors.has_loading_dock && (
                    <p className="signup-page__field-error">{errors.has_loading_dock}</p>
                  )}
                </div>
              )}
            </div>

            {/* ── How Did You Hear ── */}
            <div className="signup-page__section">
              <p className="signup-page__section-eyebrow">
                How Did You Hear About Us?{' '}
                <span className="signup-page__optional">(optional)</span>
              </p>
              <textarea
                name="how_did_you_hear"
                className={`signup-page__textarea${errors.how_did_you_hear ? ' signup-page__textarea--error' : ''}`}
                value={formData.how_did_you_hear}
                onChange={(e) => handleChange('how_did_you_hear', e.target.value)}
                placeholder="Word of mouth, farmers market, Common Grain Alliance, social media…"
                rows={3}
                maxLength={2000}
              />
            </div>

            <button
              type="submit"
              className="signup-page__submit"
              disabled={isSigningUp}
            >
              {isSigningUp ? 'Submitting Application…' : 'Submit Application →'}
            </button>
          </form>

          <div className="signup-page__footer">
            <p>
              Already have an account?{' '}
              <Link to="/login" className="signup-page__link">Sign in</Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Signup;
